/**
 * Payment Design Tokens
 * Figma design system colors and styles for mobile payment components
 * Based on Figma designs: https://www.figma.com/design/5JJxK95E3SWdTaU7EfcuFZ/Resio---Web-portal
 */
import { useMemo } from 'react';
import { SPACING } from '@/constants/shared/theme/spacing';
import { RADIUS } from '@/constants/shared/theme/radius';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

/**
 * ⚠️ Static, dark-only. Renders one fixed theme regardless of the active one.
 * Use `usePaymentDesignTokens()` below; this remains only as the source
 * `audit:palette-parity` diffs the hook against, and goes away with the last
 * importer.
 *
 * Trimmed 2026-08-03 from 33 keys to the 20 with a consumer. The 13 removed
 * (`BADGE_*`, `BUTTON_SECONDARY*`, `BUTTON_OUTLINE_*`, `GRADIENT_*`,
 * `BORDER_LIGHT`, `TABLE_HEADER_TEXT`, `TABLE_ROW_BORDER`) were referenced
 * nowhere, so they would have cost light-mode values for no rendered pixel.
 */
export const PAYMENT_DESIGN_TOKENS = {
  // Backgrounds
  BACKGROUND: '#171717',
  CARD_BACKGROUND: '#2F2F2F',
  CARD_BACKGROUND_SECONDARY: '#242424',

  // Text
  TEXT_PRIMARY: '#EEF1F4',
  TEXT_MUTED: '#919191',
  TEXT_SECONDARY: '#ACACAC',
  TEXT_TERTIARY: '#464646',

  // Accents
  SUCCESS_GREEN: '#5CBC5D',
  PINK_ACCENT: '#F55560',
  BLUE_LINK: '#6B8EFE',
  ERROR_RED: '#F55560',

  // Borders
  BORDER: '#464646',

  // Buttons
  BUTTON_PRIMARY: PRIMITIVE_COLORS.resioBlue,
  BUTTON_PRIMARY_TEXT: '#FFFFFF',

  // Avatar
  AVATAR_BACKGROUND: '#464646',
  AVATAR_TEXT: '#EEF1F4',

  // Overlay
  OVERLAY_BACKGROUND: 'rgba(0, 0, 0, 0.75)',

  // Table
  TABLE_AMOUNT_POSITIVE: '#5CBC5D',
  TABLE_AMOUNT_NEGATIVE: '#F55560',
  TABLE_AMOUNT_NEUTRAL: '#EEF1F4',
} as const;

/**
 * Theme-aware twin of PAYMENT_DESIGN_TOKENS. Identical keys, so adopting it is
 * an import swap plus a hook call.
 *
 * 17 of the 20 keys resolve to a theme token whose dark value is byte-identical
 * to the static one, so dark is a no-op for them and the light value already
 * existed. The three that are not:
 *
 * - SUCCESS_GREEN / TABLE_AMOUNT_POSITIVE were `#5CBC5D`, a fourth green with
 *   no equivalent anywhere. They now use `status.successText`, added for this:
 *   `status.success` is an indicator color that measures 2.78:1 in light and
 *   cannot carry the green *text* these keys are used for. Dark shifts
 *   `#5CBC5D` → `#66CE66` (5.61:1 → 6.75:1 on a card).
 * - OVERLAY_BACKGROUND stays a dark scrim in both themes — it dims whatever is
 *   behind a modal, which is not a theme-dependent job. It keeps its own 0.75
 *   rather than `background.overlay`'s 0.35, which was tuned for a different
 *   surface.
 */
export function usePaymentDesignTokens() {
  const { semantic, components } = useThemeColors();
  return useMemo(
    () =>
      ({
        BACKGROUND: semantic.background.primary,
        CARD_BACKGROUND: semantic.background.tertiary,
        CARD_BACKGROUND_SECONDARY: semantic.background.secondary,

        TEXT_PRIMARY: semantic.text.primary,
        TEXT_MUTED: semantic.text.tertiary,
        TEXT_SECONDARY: semantic.text.secondary,
        // Named "text" but used for disabled icons, a divider and a disabled
        // label — `#464646` is a de-emphasis tone, not an ink. `interactive.
        // disabled` is the token that means that in both themes.
        TEXT_TERTIARY: semantic.interactive.disabled,

        SUCCESS_GREEN: semantic.status.successText,
        PINK_ACCENT: semantic.interactive.secondary,
        BLUE_LINK: semantic.text.link,
        ERROR_RED: semantic.interactive.secondary,

        BORDER: semantic.border.secondary,

        BUTTON_PRIMARY: semantic.interactive.primary,
        BUTTON_PRIMARY_TEXT: components.button.primaryText,

        AVATAR_BACKGROUND: semantic.border.secondary,
        AVATAR_TEXT: semantic.text.primary,

        OVERLAY_BACKGROUND: 'rgba(0, 0, 0, 0.75)',

        TABLE_AMOUNT_POSITIVE: semantic.status.successText,
        TABLE_AMOUNT_NEGATIVE: semantic.interactive.secondary,
        TABLE_AMOUNT_NEUTRAL: semantic.text.primary,
      }) as const,
    [semantic, components],
  );
}

/** @deprecated use the named PAYMENT_TYPOGRAPHY export from @/constants/shared/theme — a
 * distinct scale; useTypography() returns only the primary TYPOGRAPHY scale. */
export { PAYMENT_TYPOGRAPHY } from '@/constants/shared/theme/typography';

/** @deprecated use useSpacing() from '@/hooks/shared/useSpacing' */
export const PAYMENT_SPACING = {
  xs: SPACING.xs,
  sm: SPACING.sm,
  md: SPACING.md,
  lg: SPACING.lg,
  xl: SPACING.xl,
  xxl: SPACING['2xl'],
  xxxl: SPACING['3xl'],
} as const;

/**
 * @deprecated use useRadius() from '@/hooks/shared/useRadius'.
 *
 * ⚠️ INTENTIONAL KEY OFFSET: these keys are deliberately mapped one step DOWN
 * the RADIUS scale to preserve the original payment values (e.g. PAYMENT.xl = 16
 * = RADIUS.lg, NOT RADIUS.xl = 24). Do NOT "align" the names to matching RADIUS
 * keys — that silently changes every payment radius. Migrate value-by-value.
 */
export const PAYMENT_BORDER_RADIUS = {
  sm: RADIUS.xs,
  md: RADIUS.sm,
  lg: RADIUS.md,
  xl: RADIUS.lg,
  xxl: RADIUS.xl,
  full: RADIUS.full,
} as const;

// Avatar sizes
export const PAYMENT_AVATAR_SIZE = {
  sm: 40,
  md: 56,
  lg: 68,
} as const;
